import React, { useState, useCallback } from 'react';
import { rootStore } from '../../../../mobx/rootStore';
import {
  addMembersInMessageGroup,
  deleteGroupMessage,
  pinMessageGroup,
  removeMemberFromMessageGroup,
  unPinMessageGroup,
  updateMessageGroup
} from '../../../../services/chatMessageService';
import UserPreferenceSingleton from '../../../../helpers/userPreferenceSingleton';
import { ROUTES } from '../../../../routes/urls';
import { useHistory } from 'react-router-dom';
import { captureException } from '../../../../services/logService';
import { ModalName } from './useGroupModals';

export interface UseGroupActionsProps {
  loadData?: () => void;
  close?: (modal: ModalName) => void;
  removeUserId?: string;
}

export const useGroupActions = ({ loadData, close, removeUserId }: UseGroupActionsProps) => {
  // state variable
  const [submitLoading, setSubmitLoading] = useState(false);
  const [clearMessageLoading, setClearMessageLoading] = useState(false);
  const [updateGroupLoading, setUpdateGroupLoading] = useState(false);
  // mobx store variable
  const { chatMessageStore } = rootStore;
  const { chatGroups, pinChatGroups, unpinChatGroups, inputChatGroup, currentChatGroup } = chatMessageStore;
  // other variables
  const history = useHistory();

  // update chat groups
  const updateChatGroups = useCallback(
    (groupId: string, updates: { pinned: boolean; order: number | undefined }) => {
      return chatGroups?.map((group) => {
        if (group.id === groupId) {
          return {
            ...group,
            ...updates
          };
        }
        return group;
      });
    },
    [chatGroups]
  );

  const handlePinChatGroup = useCallback(async () => {
    if (!currentChatGroup?.id) return;

    const willPin = !currentChatGroup.pinned;
    const updatedCurrentGroup = {
      ...(currentChatGroup || {}),
      pinned: willPin,
      order: willPin ? new Date().getTime() : undefined
    };

    try {
      const response = willPin
        ? await pinMessageGroup(currentChatGroup.id)
        : await unPinMessageGroup(currentChatGroup.id);

      if (response) {
        const updatedChatGroups = updateChatGroups(currentChatGroup.id, {
          pinned: willPin,
          order: updatedCurrentGroup.order
        });

        if (willPin) {
          chatMessageStore.setPinChatGroups([...(pinChatGroups || []), updatedCurrentGroup]);
          chatMessageStore.setUnpinChatGroups(unpinChatGroups?.filter((group) => group.id !== currentChatGroup.id));
        } else {
          const unpinnedGroups = updatedChatGroups?.filter(
            (group) => group?.order === undefined || group?.order === null
          );
          chatMessageStore.setUnpinChatGroups(unpinnedGroups);
          chatMessageStore.setPinChatGroups(pinChatGroups?.filter((group) => group.id !== currentChatGroup.id));
        }

        chatMessageStore.setChatGroups(updatedChatGroups);
        chatMessageStore.setCurrentChatGroup(updatedCurrentGroup);
      }
    } catch (error) {
      captureException(error);
    }
  }, [currentChatGroup, chatMessageStore, pinChatGroups, unpinChatGroups, updateChatGroups]);

  // delete group message
  const onDeleteGroupMessage = useCallback(async () => {
    setClearMessageLoading(true);
    try {
      const result = await deleteGroupMessage(currentChatGroup?.type, currentChatGroup?.id);
      if (result) {
        close?.('clearMessage');
        loadData?.();
      }
    } catch (e) {
      captureException(e);
    } finally {
      setClearMessageLoading(false);
    }
  }, [currentChatGroup?.type, currentChatGroup?.id, close, loadData]);

  // update group name
  const onClickSave = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      try {
        setUpdateGroupLoading(true);
        const payload = {
          name: String(inputChatGroup?.name || '')
        };
        const result = await updateMessageGroup(currentChatGroup?.id || '', payload);
        if (result) {
          close?.('editGroup');
          loadData?.();
        }
      } catch (e) {
        captureException(e);
      } finally {
        setUpdateGroupLoading(false);
      }
    },
    [inputChatGroup?.name, currentChatGroup?.id, close, loadData]
  );

  // add members in message group
  const onAddMembers = useCallback(
    async (e: React.SyntheticEvent, usersList: string[]) => {
      try {
        e.preventDefault();
        setSubmitLoading(true);

        if (usersList?.length > 0) {
          const payload = {
            users: usersList
          };
          const result = await addMembersInMessageGroup(currentChatGroup?.id || '', payload);
          if (result) {
            close?.('member');
            loadData?.();
          }
        } else {
          rootStore.alertNotificationStore.setErrorMessage('Please select user');
        }
      } catch (error) {
        captureException(error);
        setSubmitLoading(false);
      } finally {
        setSubmitLoading(false);
      }
    },
    [currentChatGroup?.id, close, loadData]
  );

  // remove user from group message
  const onClickRemoveUser = useCallback(async () => {
    try {
      setSubmitLoading(true);
      const currentUser = UserPreferenceSingleton.getInstance().getCurrentUser();
      const result = await removeMemberFromMessageGroup(currentChatGroup?.id, currentUser?.id, removeUserId || '');
      if (result) {
        close?.('alert');
        if (currentUser?.id === removeUserId) {
          loadData?.();
          history.push(ROUTES.NAVBARMESSAGE);
        } else loadData?.();
      }
    } catch (error) {
      captureException(error);
      setSubmitLoading(false);
    } finally {
      setSubmitLoading(false);
    }
  }, [close, currentChatGroup?.id, history, loadData, removeUserId]);

  // change group name
  const onChangeGroupName = useCallback(
    (propsName: string, value: string) => {
      chatMessageStore.updateCurrentChatGroupName(propsName, value);
    },
    [chatMessageStore]
  );

  return {
    loading: {
      submit: submitLoading,
      clearMessage: clearMessageLoading,
      update: updateGroupLoading
    },
    actions: {
      handlePinChatGroup,
      onDeleteGroupMessage,
      onClickSave,
      onAddMembers,
      onClickRemoveUser,
      onChangeGroupName
    }
  };
};
